"use client"

import { keepPreviousData, useQuery } from "@tanstack/react-query"

import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"
import { campaignKeys } from "@/lib/apps/autoleads/keys/campaign"
import { useStaffSupervisor } from "@/lib/apps/autoleads/staff-supervisor-cookie"

import type {
  Campaign,
  CampaignDigest,
  CampaignListItem,
  CampaignMailingLeadsResponse,
  CampaignProjection,
  CampaignPublic,
  CampaignTargets,
  Lead,
  ReenrichPluginSlug,
  ReenrichPreview,
  ReenrichStatus,
} from "@/nango/types/apps.autoleads/autoleads/AutoLeads"
import type { CostBreakdown } from "@/nango/types/apps.autoleads/autoleads/Tick"

export type CampaignProgress = {
  is_running: boolean
  running_tick_id: number | null
  stages: {
    key: string
    total: number
    succeeded: number
    failed: number
    running: number
    pct: number
  }[]
  overall_pct: number
  leads_count: number
  tick_count: number
}

// ``teamId`` is the team the UI is currently showing. The backend needs it to
// resolve which team's demo dismissals apply: a user in several teams archives
// a shared demo per team, not everywhere at once. It is required, so a caller
// that does not know the active team yet cannot silently fall back to the
// team-agnostic list below: pass ``enabled: false`` and wait instead.
export function useCampaigns(
  enabled: boolean,
  archived: boolean,
  teamId: number,
) {
  const params = new URLSearchParams()
  if (archived) params.set("archived", "true")
  params.set("team_id", String(teamId))
  return useQuery({
    queryKey: campaignKeys.list(archived, teamId),
    queryFn: () =>
      apiClient<CampaignListItem[]>({
        url: apiUrl(`api/al/campaign/?${params.toString()}`),
        method: "GET",
      }),
    enabled,
  })
}

// Every non-archived campaign of every team the user belongs to, for the
// callers that count them or look for a demo rather than showing one team's
// list (the sidebar badge, the tour target, the import picker).
export function useAllCampaigns(enabled: boolean = true) {
  return useQuery({
    queryKey: campaignKeys.listAll(),
    queryFn: () =>
      apiClient<CampaignListItem[]>({
        url: apiUrl("api/al/campaign/"),
        method: "GET",
      }),
    enabled,
  })
}

export type StaffCampaignFilters = {
  status?: string
  team_id?: number
  q?: string
}

export type StaffCampaign = Campaign & {
  team_meta?: { id: number; name: string; owner_email: string }
}

export function useStaffCampaigns(
  filters: StaffCampaignFilters = {},
  enabled: boolean = true,
) {
  const params = new URLSearchParams()
  if (filters.status) params.set("status", filters.status)
  if (filters.team_id != null) params.set("team_id", String(filters.team_id))
  if (filters.q) params.set("q", filters.q)
  const qs = params.toString()
  return useQuery({
    queryKey: campaignKeys.staffList(filters),
    queryFn: () =>
      apiClient<StaffCampaign[]>({
        url: apiUrl(`api/al/staff_campaign/${qs ? `?${qs}` : ""}`),
        method: "GET",
      }),
    enabled,
  })
}

export function useStaffCampaign(id: string | number, enabled: boolean = true) {
  return useQuery({
    queryKey: campaignKeys.staffDetail(id),
    queryFn: () =>
      apiClient<StaffCampaign>({
        url: apiUrl(`api/al/staff_campaign/${id}/`),
        method: "GET",
      }),
    enabled,
  })
}

export { useStaffSupervisor }

export function useMailingCampaigns(enabled: boolean = true) {
  return useQuery({
    queryKey: campaignKeys.mailingList(),
    queryFn: () =>
      apiClient<Campaign[]>({
        url: apiUrl("api/al/campaign/mailing/"),
        method: "GET",
      }),
    enabled,
  })
}

export function useCampaign(id: string | number, enabled: boolean = true) {
  return useQuery({
    queryKey: campaignKeys.detail(id),
    queryFn: () =>
      apiClient<Campaign>({
        url: apiUrl(`api/al/campaign/${id}/`),
        method: "GET",
      }),
    enabled,
  })
}

export function useCampaignMailingLeads(
  id: string | number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: campaignKeys.mailingLeads(id),
    queryFn: () =>
      apiClient<CampaignMailingLeadsResponse>({
        url: apiUrl(`api/al/campaign/${id}/mailing_leads/`),
        method: "GET",
      }),
    enabled,
  })
}

export function useCampaignCostBreakdown(
  id: string | number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: campaignKeys.costBreakdown(id),
    queryFn: () =>
      apiClient<CostBreakdown>({
        url: apiUrl(`api/al/campaign/${id}/cost_breakdown/`),
        method: "GET",
      }),
    enabled,
  })
}

export function useCampaignDigest(
  id: string | number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: campaignKeys.digest(id),
    queryFn: () =>
      apiClient<CampaignDigest>({
        url: apiUrl(`api/al/campaign/${id}/digest/`),
        method: "GET",
      }),
    enabled,
    // The debrief is a "since your last visit" snapshot: refetching it while
    // the dialog is open would shrink the numbers under the user's eyes.
    refetchOnWindowFocus: false,
  })
}

export function useCampaignProgress(
  id: string | number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: campaignKeys.progress(id),
    queryFn: () =>
      apiClient<CampaignProgress>({
        url: apiUrl(`api/al/campaign/${id}/progress/`),
        method: "GET",
      }),
    enabled,
  })
}

// ``lead_ids`` null = every lead; ``plugins`` null = the plugins whose config
// changed since the leads last ran them.
export type ReenrichRequest = {
  lead_ids: number[] | null
  plugins: ReenrichPluginSlug[] | null
}

export function useReenrichPreview(
  id: string | number,
  request: ReenrichRequest,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: campaignKeys.reenrichPreview(id, request),
    queryFn: () =>
      apiClient<ReenrichPreview>({
        url: apiUrl(`api/al/campaign/${id}/reenrich_preview/`),
        method: "POST",
        data: request,
      }),
    enabled,
    // Toggling a plugin re-quotes: keep the last quote on screen meanwhile.
    placeholderData: keepPreviousData,
  })
}

const REENRICH_POLL_MS = 5000
const CAMPAIGN_TARGETS_STALE_MS = 60_000

// Runs still queued or running. Polls while any are left, so the lead list can
// refresh the moment the last one lands.
export function useReenrichStatus(
  id: string | number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: campaignKeys.reenrichStatus(id),
    queryFn: () =>
      apiClient<ReenrichStatus>({
        url: apiUrl(`api/al/campaign/${id}/reenrich_status/`),
        method: "GET",
      }),
    enabled,
    refetchInterval: (query) =>
      (query.state.data?.pending ?? 0) > 0 ? REENRICH_POLL_MS : false,
  })
}

// Only fetched once the "targets covered" popover opens, then kept for a
// minute: the target in progress moves at most once per tick.
export function useCampaignTargets(id: string | number, enabled: boolean) {
  return useQuery({
    queryKey: campaignKeys.targets(id),
    queryFn: () =>
      apiClient<CampaignTargets>({
        url: apiUrl(`api/al/campaign/${id}/targets/`),
        method: "GET",
      }),
    enabled,
    staleTime: CAMPAIGN_TARGETS_STALE_MS,
  })
}

export function usePublicCampaign(uuid: string, enabled: boolean = true) {
  return useQuery({
    queryKey: campaignKeys.publicByUuid(uuid),
    queryFn: () =>
      apiClient<CampaignPublic>({
        url: apiUrl(`api/al/campaign/public/${uuid}/`),
        method: "GET",
      }),
    enabled,
  })
}

export function usePublicCampaignProjection(
  uuid: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: campaignKeys.publicProjection(uuid),
    queryFn: () =>
      apiClient<CampaignProjection>({
        url: apiUrl(`api/al/campaign/public/${uuid}/projection/`),
        method: "GET",
      }),
    enabled,
  })
}

export function usePublicCampaignLeads(
  uuid: string,
  enabled: boolean = true,
  // Poll while a run is discovering, so leads stream in one by one.
  refetchIntervalMs: number | false = false,
) {
  return useQuery({
    queryKey: campaignKeys.publicLeads(uuid),
    queryFn: () =>
      apiClient<Lead[]>({
        url: apiUrl(`api/al/campaign/public/${uuid}/leads/`),
        method: "GET",
      }),
    enabled,
    refetchInterval: refetchIntervalMs,
  })
}
